import { ComponentType } from "react"
import * as DesignSystemIcons from "@rhumbix/rmbx_design_system_web"

type EodQuestionIconComponent = ComponentType<{ height?: number; width?: number; color?: string }>

/**
 * Explicit allow-list of design-system Icon* components an admin can pick for an EoD question.
 * Previously this was derived at runtime from every Icon*-prefixed export in the design system;
 * it's spelled out here instead so the team can curate it -- add a name to make a new
 * design-system icon selectable, remove one to retire it going forward. Existing questions keep
 * storing whatever name they were saved with, so removing a name here does not change
 * already-saved questions, it only hides that name from the picker for new/edited ones.
 */
export const EOD_QUESTION_ICON_NAMES: string[] = [
    "IconAbsent",
    "IconActionsRequired",
    "IconAdd",
    "IconAddBorderless",
    "IconAddCollaborator",
    "IconAddFilled",
    "IconAddModifier",
    "IconArchive",
    "IconArrowDown",
    "IconArrowsCollapse",
    "IconArrowsExpand",
    "IconAttachment",
    "IconAvatar",
    "IconBarGraph",
    "IconBlocked",
    "IconBookmark",
    "IconBookmarkFilled",
    "IconBundle",
    "IconBundleAction",
    "IconCalendarWeek",
    "IconCamera",
    "IconCameraRetake",
    "IconCheck",
    "IconCheckboxChecked",
    "IconCheckboxIndeterminate",
    "IconCheckboxUnchecked",
    "IconCheckThick",
    "IconChevronLeft",
    "IconChevronRight",
    "IconCicoDefault",
    "IconCicoDeficit",
    "IconCicoException",
    "IconCicoExcess",
    "IconCircle",
    "IconCircleFilled",
    "IconCollapseAll",
    "IconComment",
    "IconCompanySettings",
    "IconCopy",
    "IconCostCode",
    "IconCraftWorker",
    "IconCrew",
    "IconCSVDocument",
    "IconDeactivate",
    "IconDelay",
    "IconDelete",
    "IconDiamond",
    "IconDiamondFilled",
    "IconDinosaur",
    "IconDocumentError",
    "IconDocumentGeneric",
    "IconDocumentImage",
    "IconDocumentPDF",
    "IconDocumentText",
    "IconDocumentVideo",
    "IconDownload",
    "IconDownloadAll",
    "IconDuplicate",
    "IconEdit",
    "IconEditUnderline",
    "IconEmployeeBadge",
    "IconEquipment",
    "IconException",
    "IconExpandAll",
    "IconExport",
    "IconFilter",
    "IconFind",
    "IconFlag",
    "IconFolder",
    "IconFormLibrary",
    "IconForms",
    "IconFormVariant",
    "IconGlobe",
    "IconGrip",
    "IconGroup",
    "IconHamburger",
    "IconHide",
    "IconHistory",
    "IconImport",
    "IconIncomplete",
    "IconInfoFilled",
    "IconInfoHollow",
    "IconInjured",
    "IconKabab",
    "IconKey",
    "IconLaunch",
    "IconLink",
    "IconLoading",
    "IconLocation",
    "IconLock",
    "IconMapPolygon",
    "IconMapRadius",
    "IconMaterials",
    "IconMeal",
    "IconMeasurement",
    "IconMeatball",
    "IconMisc",
    "IconNext",
    "IconNote",
    "IconOff",
    "IconOffline",
    "IconOnline",
    "IconPalmTree",
    "IconPalmTreeFilled",
    "IconPaste",
    "IconPrint",
    "IconRadioButtonSelected",
    "IconRadioButtonUnselected",
    "IconRefresh",
    "IconReject",
    "IconRemove",
    "IconReports",
    "IconSafety",
    "IconSearch",
    "IconSettings",
    "IconShare",
    "IconShiftExtra",
    "IconShoveling",
    "IconSignature",
    "IconSignatureLightbox",
    "IconSigned",
    "IconSortOrder",
    "IconSortOrderFlipped",
    "IconStatus",
    "IconStretch",
    "IconSubmittedTime",
    "IconSubtract",
    "IconSuccess",
    "IconText",
    "IconTime",
    "IconTimeCard",
    "IconTrash",
    "IconTriangle",
    "IconTriangleFilled",
    "IconUndo",
    "IconUpload",
    "IconUser",
    "IconView",
    "IconWarning",
    "IconWarningFilled",
    "IconX",
]

/** Lookup from an icon name (as stored on EodQuestionVersion.icon) to its design-system component. */
export const EOD_QUESTION_ICONS: Record<string, EodQuestionIconComponent> = Object.fromEntries(
    EOD_QUESTION_ICON_NAMES.map(name => [name, ((DesignSystemIcons as unknown) as Record<string, EodQuestionIconComponent>)[name]])
)
